import { SignupButton } from '@src/components/signup-button/SignupButton.component'
import { languageContext } from '@src/context/language.context'
import { Breakpoints, useBreakpoint } from '@src/hooks/useBreakpoint'
import classNames from 'classnames'
import type { ReactNode } from 'react'
import { useContext, useEffect, useState } from 'react'
import { useScrollPosition } from 'react-use-scroll-position'
import { ClientOnly } from '../client-only/ClientOnly.component'
import styles from './TrailingButton.module.scss'

export interface TrailingButtonProps {
  button: {
    text: string
  }[]
  paragraph?: {
    field: string
  }[]
  page?: 'RaF' | 'homepage'
}

export function TrailingButton({
  button,
  paragraph,
  page = 'RaF',
}: TrailingButtonProps): ReactNode {
  const [visible, setVisible] = useState(false)
  // We only want to slide out on a transition from visible to invisible
  const [wasVisible, setWasVisible] = useState(false)

  const windowHasLoaded = typeof window !== 'undefined'
  const isReferralOffer = windowHasLoaded && window.sessionStorage.getItem('discountReferral')

  const storedPercentage = windowHasLoaded && window.sessionStorage.getItem('discountPercentage')
  let percentage = storedPercentage && isReferralOffer ? storedPercentage : '20% off'

  const { locale } = useContext(languageContext)

  if (locale !== 'en-US') {
    percentage = percentage.substring(0, percentage.length - 4)
  }

  const scrollDepth = useScrollPosition().y
  const footerHeight = useBreakpoint().width < Breakpoints.bpMedium ? 117 : 85

  useEffect(() => {
    const fullyScrolledDown =
      window.innerHeight + window.scrollY >= document.body.scrollHeight - footerHeight

    const isHomepage = page === 'homepage'
    const isAllowedToBeVisible = isHomepage || !fullyScrolledDown

    if (scrollDepth > 300 && isAllowedToBeVisible) {
      if (!visible) {
        setWasVisible(false)
      }
      setVisible(true)
    } else {
      if (visible) {
        setWasVisible(true)
      }
      setVisible(false)
    }
  }, [scrollDepth])

  const trailingButton = classNames(styles.TrailingButton, styles[`TrailingButton___${page}`], {
    [styles.TrailingButton___invisible]: !visible, // Determines position
    [styles.TrailingButton___slideOut]: wasVisible && !visible, // Determines animation
  })

  return (
    // This component depends on the window so we client-only it
    <ClientOnly>
      <div className={trailingButton}>
        <div className={styles.TrailingButton__button}>
          <SignupButton
            ncUtmSetSource="trailing_button_signup"
            theme={page === 'RaF' ? 'shimmerGradient' : 'brandPurple'}
          >
            {button[0]?.text}
          </SignupButton>
        </div>

        {paragraph && (
          <p className={styles.TrailingButton__text}>
            {paragraph[0]?.field} {percentage} {paragraph[1]?.field}
          </p>
        )}
      </div>
    </ClientOnly>
  )
}
